'use client'

import React, { useEffect, useState } from 'react'
import { Button, useDocumentInfo } from '@payloadcms/ui'
import { errMessage } from '../../lib/errors'

/**
 * La FINESTRA DI ANNULLO nella edit view di un articolo (slot beforeDocumentControls).
 *
 * Alla pubblicazione l'invio agli iscritti non parte subito: viene messo in coda con qualche
 * minuto di ritardo, e questo riquadro è il posto dove quei minuti si vedono e si usano. Mostra
 * quanto manca e offre le due sole azioni che servono davvero: fermarlo, o non aspettare.
 *
 * Legge lo stato dai campi del form (`newsletter.*`, scritti dal worker) invece di interrogare un
 * endpoint: dopo un'azione la pagina si ricarica, quindi i valori sono quelli veri del documento e
 * non una copia che invecchia. Il conto alla rovescia si aggiorna da sé ogni quindici secondi, che
 * è anche il passo con cui il worker guarda la coda: mostrare i secondi darebbe una precisione che
 * il sistema non ha.
 */

const ENDPOINT = '/api/newsletter-send'

/**
 * Le frasi per i motivi che l'endpoint può rispondere con `ok: false`. Sono risposte 200: senza
 * questa mappa il riquadro si ricaricava identico e il pulsante sembrava non fare niente, che è il
 * modo migliore per farlo premere una seconda volta. Un motivo che non è qui dentro si mostra lo
 * stesso, col suo codice: meglio una frase brutta che il silenzio.
 */
const MOTIVI: Record<string, string> = {
  'invio-gia-partito': "L'invio era già partito: non è stato fermato. Controlla quanti iscritti sono stati raggiunti.",
  'invio-gia-in-corso': "L'invio è già in corso: aspetta che finisca prima di rilanciarlo.",
  'niente-da-annullare': 'Non c’è nessun invio programmato da annullare.',
  'annullamento-non-riuscito': "Il job programmato non è stato cancellato: l'invio partirà lo stesso. Riprova fra qualche secondo.",
}

const styles: Record<string, React.CSSProperties> = {
  box: {
    alignItems: 'center',
    background: 'var(--theme-elevation-50)',
    border: '1px solid var(--theme-elevation-150)',
    borderRadius: 'var(--style-radius-s)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    justifyContent: 'space-between',
    padding: '0.6rem 0.9rem',
    width: '100%',
    marginBottom: '1.25rem',
  },
  pending: { background: 'var(--theme-warning-50)', border: '1px solid var(--theme-warning-250)' },
  text: { fontSize: '0.85rem', lineHeight: 1.45, minWidth: 0 },
  actions: { display: 'flex', flexShrink: 0, gap: '0.4rem' },
}

/** Minuti che mancano, arrotondati per eccesso: «tra 1 minuto» fino allo scadere, poi «a momenti». */
function minutesLeft(iso?: string | null): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  if (!Number.isFinite(ms)) return null
  return ms <= 0 ? 0 : Math.ceil(ms / 60_000)
}

/**
 * Lo stato newsletter del documento, letto dal SERVER e non dai campi del form. I valori di
 * `newsletter.*` li scrive il worker mentre la pagina è aperta, quindi nel form state possono
 * non esserci affatto (il form conosce i campi caricati all'apertura) e il riquadro sparirebbe
 * proprio nei minuti in cui serve. Una GET su /api/posts/<id> dice sempre la verità.
 *
 * Non lancia mai: su guasto torna un oggetto vuoto, che il chiamante rende come «nessuno stato».
 */
const readNewsletter = async (id: string | number): Promise<Record<string, unknown>> => {
  try {
    const res = await fetch(`/api/posts/${id}?depth=0`, { credentials: 'include' })
    const doc = (await res.json().catch(() => null)) as { newsletter?: unknown } | null
    const n = doc?.newsletter
    return typeof n === 'object' && n !== null ? (n as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

export default function NewsletterSendWindow() {
  const { id } = useDocumentInfo()
  const [state, setState] = useState<Record<string, unknown> | null>(null)
  const [busy, setBusy] = useState(false)
  const [, forceTick] = useState(0)

  useEffect(() => {
    if (!id) return
    let alive = true
    void readNewsletter(id).then((doc) => {
      if (alive) setState(doc)
    })
    return () => {
      alive = false
    }
  }, [id])

  const status = state?.status as string | undefined
  const scheduledAt = state?.scheduledAt as string | undefined
  const recipients = state?.recipients as number | undefined
  const sentAt = state?.sentAt as string | undefined
  const error = state?.error as string | undefined

  // Il riquadro conta alla rovescia da solo: senza questo, «tra 5 minuti» resta scritto anche
  // quando i minuti sono passati, e chi guarda crede di avere tempo che non ha più.
  useEffect(() => {
    if (status !== 'programmato' || !id) return
    const t = setInterval(() => {
      forceTick((n) => n + 1)
      void readNewsletter(id).then(setState)
    }, 15_000)
    return () => clearInterval(t)
  }, [status, id])

  if (!id || !status) return null

  const call = async (action: 'cancel' | 'now') => {
    setBusy(true)
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id, action }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        window.alert(`Errore: ${data?.error || res.status}`)
        return
      }
      if (data?.ok === false) {
        window.alert(MOTIVI[String(data?.reason)] ?? `Operazione non riuscita (${data?.reason ?? 'motivo sconosciuto'}).`)
      }
      window.location.reload()
    } catch (e: unknown) {
      window.alert(`Errore: ${errMessage(e)}`)
    } finally {
      setBusy(false)
    }
  }

  const sendNow = async () => {
    if (sentAt) {
      const again = window.confirm(
        'Questo articolo è già stato mandato agli iscritti. Vuoi mandarlo una seconda volta?\n\nChi lo ha già ricevuto lo riceverà di nuovo.',
      )
      if (!again) return
    }
    await call('now')
  }

  const left = minutesLeft(scheduledAt)
  const programmato = status === 'programmato'

  const attesa =
    left === null
      ? 'Invio agli iscritti programmato.'
      : left === 0
        ? 'Invio agli iscritti a momenti: se vuoi fermarlo, adesso.'
        : `Invio agli iscritti tra ${left} ${left === 1 ? 'minuto' : 'minuti'}. Fino a quel momento puoi annullarlo.`

  // L'ERRORE SI MOSTRA ANCHE DA «PROGRAMMATO». In quello stato ci si torna anche dopo un tentativo
  // fallito (il job riparte col backoff): senza questa riga il riquadro annunciava solo la prossima
  // partenza e il motivo del fallimento non compariva da nessuna parte fino all'ultimo tentativo.
  const message = programmato
    ? `${attesa}${error ? ` ${error}` : ''}`
    : status === 'in corso'
      ? 'Invio agli iscritti in corso.'
      : status === 'inviato'
        ? `Inviato a ${recipients ?? 0} iscritti${sentAt ? ` il ${new Date(sentAt).toLocaleString('it-IT')}` : ''}.${error ? ` ${error}` : ''}`
        : status === 'annullato'
          ? 'Invio annullato: agli iscritti non è arrivato niente.'
          : `Non inviato${error ? `: ${error}` : '.'}`

  return (
    <div style={{ ...styles.box, ...(programmato ? styles.pending : null) }}>
      <span style={styles.text}>{message}</span>
      <span style={styles.actions}>
        {programmato ? (
          <Button buttonStyle="secondary" size="small" onClick={() => call('cancel')} disabled={busy}>
            {busy ? 'Attendi…' : 'Annulla invio'}
          </Button>
        ) : null}
        {status !== 'in corso' ? (
          <Button buttonStyle="secondary" size="small" onClick={sendNow} disabled={busy}>
            {busy ? 'Attendi…' : programmato ? 'Invia ora' : sentAt ? 'Invia di nuovo' : 'Invia agli iscritti'}
          </Button>
        ) : null}
      </span>
    </div>
  )
}
